import {
  Button,
  Divider,
  Flex,
  Text,
  SelectField,
  TextAreaField,
  Heading,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
} from "@aws-amplify/ui-react";
import { Modal } from "rsuite";
import { v4 as uuidv4 } from "uuid";
import FlexFormContainer from "../common/FlexFormContainer";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useEncounterPostMutation } from "./api/useEncountersMutation";
import { useUserQuery } from "../common/useUserQuery";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import VitalsAddPage from "../healthRecords/VitalsAddPage";
import {
  useVitalsDeleteMutation,
  useVitalsPutMutation,
} from "../healthRecords/api/useVitalsMutation";

const postRequest = z.object({
  id: z.string().uuid().nullish(),
  overallStatus: z
    .string({ message: "Overall Status is Required" })
    .min(1, { message: "Overall Status is Required" })
    .max(50, { message: "Must be 50 or fewer characters long" }),
  outcomeText: z
    .string({ message: "Outcome Text is Required" })
    .min(1, { message: "Outcome Text is Required" }),
});

export default function EncounterAddPage() {
  const encounterId = uuidv4();
  const [unmapVitals, setUnmapVitals] = useState([]);
  const [open, setOpen] = useState(false);
  const { mutate: vitalDelete } = useVitalsDeleteMutation();
  const { mutate: vitalPut } = useVitalsPutMutation();

  const { data: userQueryData } = useUserQuery();
  const { providerId } = useParams();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(postRequest) });
  const navigate = useNavigate();
  const {
    mutate: encounterPostMutation,
    isLoading,
    error,
    isSuccess,
  } = useEncounterPostMutation();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleVitalDelete = (vitalId, index) => {
    let cloneArray = Object.assign([], unmapVitals);
    cloneArray.splice(index, 1);
    setUnmapVitals(cloneArray);
    vitalDelete(vitalId);
  };

  const handleVitalPostAdd = (vitalItem) => {
    setUnmapVitals((prev) => [...prev, vitalItem]);
    handleClose();
  };

  const onSubmit = (formValues) => {
    console.log("onSubmit");
    encounterPostMutation({
      id: encounterId,
      patientUserId: userQueryData?.patientUserId,
      providerUserId: providerId,
      ...formValues,
    });
    if (unmapVitals && unmapVitals.length > 0) {
      for (let vitalItem of unmapVitals) {
        console.log("putting vital with encounter", {
          ...vitalItem,
          encounterId: encounterId,
        });
        vitalPut({ ...vitalItem, encounterId: encounterId });
      }
    }
  };

  if (isLoading) return "Saving changes...";

  if (error)
    return "An error happened while saving changes. Please try again later.";

  if (isSuccess) {
    navigate(`/user/hr/health-providers/${providerId}`);
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexFormContainer headerTitle={"Add Encounter"}>
          <Flex>
            <Controller
              name="overallStatus"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Overall Status"
                  placeholder="Select"
                  {...field}
                  errorMessage={errors.overallStatus?.message}
                  hasError={!!errors.overallStatus}
                >
                  <option value="GOOD">GOOD</option>
                  <option value="BAD">BAD</option>
                  <option value="NEEDS_ATTENTION">NEEDS ATTENTION</option>
                </SelectField>
              )}
            />
          </Flex>
          <Flex>
            <Controller
              name="outcomeText"
              control={control}
              render={({ field }) => (
                <TextAreaField
                  label="Outcome"
                  placeholder="What were the results of the encounter?"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.outcomeText?.message}
                  hasError={!!errors.outcomeText}
                />
              )}
            />
          </Flex>
          <Divider size="small" />

          <Flex
            justifyContent={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Heading>Vitals</Heading>
            <Button children="Add" size="small" onClick={() => handleOpen()} />
          </Flex>

          {unmapVitals?.length > 0 && (
            <Flex direction={"column"}>
              <Alert
                variation="info"
                children="Please make sure to click on the SAVE button below to get your vitals attached to this encounter."
              />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell as="th">Actions</TableCell>
                    <TableCell as="th">Type</TableCell>
                    <TableCell as="th">Date</TableCell>
                    <TableCell as="th">Value</TableCell>
                    <TableCell as="th">Unit</TableCell>
                    <TableCell as="th">Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unmapVitals?.map((vital, index) => (
                    <TableRow key={vital?.id}>
                      <TableCell>
                        <Flex direction="row" alignItems="center" gap={6}>
                          <Button
                            children="Delete"
                            size="small"
                            variation="destructive"
                            onClick={() => handleVitalDelete(vital?.id, index)}
                          />
                        </Flex>
                      </TableCell>
                      <TableCell>{vital?.type}</TableCell>
                      <TableCell>{vital?.date}</TableCell>
                      <TableCell>{vital?.value}</TableCell>
                      <TableCell>{vital?.unit}</TableCell>
                      <TableCell>{vital?.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>
          )}

          <Divider size="small" />

          <Button variation="primary" children="Save" type="submit" />
        </FlexFormContainer>
      </form>
      <Modal open={open} onClose={handleClose}>
        <Modal.Header>
          <Modal.Title></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <VitalsAddPage vitalUUID={uuidv4()} onPostAdd={handleVitalPostAdd} />
        </Modal.Body>
      </Modal>
    </>
  );
}
